/**
 * 파일 속성의 조각 (캘린더 속성 spec §4.3, 백로그 AY5): 패널의 편집·읽기 칸이 같이 쓴다.
 * - fileTile: 그림은 썸네일(`?inline=1` — 서버는 그림만 inline 으로 준다), PDF 는 PDF 표시, 그 밖은 📎
 * - uploadFiles: 여러 파일을 **차례로** 올린다(서버는 한 번에 하나, 일정당 10개 · 10 MB). 남은 자리·크기는
 *   먼저 보고 넘치는 파일은 건너뛰며, 각 실패는 서버 문장으로 알린다. 끝나면 마지막 props 목록을 돌려준다.
 * - bindDrop: 칸에 끌어 놓기 (드래그 중 `.is-drop`)
 */
export const CAL_MAX_FILES = 10;
export const CAL_MAX_FILE_BYTES = 10 * 1024 * 1024;

const IMG = /\.(png|jpe?g|gif|webp)$/i;
export function fileKind(name) {
    if (IMG.test(name || '')) return 'image';
    if (/\.pdf$/i.test(name || '')) return 'pdf';
    return 'file';
}
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
function sizeText(n) {
    if (!n) return '';
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB';
    return (n / 1024 / 1024).toFixed(1) + ' MB';
}
export function fileHref(evId, f) { return '/calendar/events/' + encodeURIComponent(evId) + '/files/' + encodeURIComponent(f.id); }

// 공용 첨부 보기 창(file-viewer.js)이 여는 링크 — 미리 받기에서도 뺀다.
function viewable(a, f) {
    a.setAttribute('data-no-prefetch', '');
    a.setAttribute('data-file-view', '');
    a.dataset.name = f.name || '';
    if (f.size) a.dataset.size = sizeText(f.size);
}

// 한 줄: [미리보기] 이름(보기 창으로 열림) · 크기 [✕]
export function fileTile(evId, f, opts) {
    const o = opts || {};
    const row = el('div', 'cal-prop-file');
    row.dataset.fileId = f.id;
    const kind = fileKind(f.name);
    const href = fileHref(evId, f);
    const prev = el('a', 'cal-prop-file-prev cal-prop-file-' + kind);
    prev.href = href; prev.setAttribute('data-no-spa', ''); prev.setAttribute('tabindex', '-1'); prev.setAttribute('aria-hidden', 'true');
    viewable(prev, f);
    if (kind === 'image') {
        const img = el('img'); img.loading = 'lazy'; img.alt = ''; img.src = href + '?inline=1';
        img.addEventListener('error', () => { img.remove(); prev.textContent = '🖼'; });
        prev.appendChild(img);
    } else {
        prev.textContent = kind === 'pdf' ? 'PDF' : '📎';
    }
    row.appendChild(prev);
    const a = el('a', 'cal-prop-file-name', f.name); a.href = href; a.setAttribute('data-no-spa', '');
    viewable(a, f);
    row.appendChild(a);
    if (f.size) row.appendChild(el('span', 'cal-prop-file-size', sizeText(f.size)));
    if (o.onRemove) {
        const x = el('button', 'icon', '✕'); x.type = 'button'; x.setAttribute('aria-label', o.removeLabel || 'remove');
        x.addEventListener('click', () => o.onRemove(f, x));
        row.appendChild(x);
    }
    return row;
}

// 여러 파일 차례로 → { props, added, errors[] }. send 는 csrfFetch 호환 함수.
export async function uploadFiles(evId, propId, files, opts) {
    const o = opts || {};
    const send = o.send || window.csrfFetch || fetch;
    let props = o.current || [];
    const errors = [];
    let added = 0;
    for (const file of Array.from(files || [])) {
        if (props.length >= CAL_MAX_FILES) { errors.push(o.labels && o.labels.limit ? o.labels.limit : 'limit'); break; }
        if (file.size > CAL_MAX_FILE_BYTES) { errors.push(file.name + ' — ' + ((o.labels && o.labels.tooLarge) || '10MB')); continue; }
        const fd = new FormData(); fd.append('file', file);
        try {
            const r = await send('/calendar/events/' + encodeURIComponent(evId) + '/files?prop=' + encodeURIComponent(propId), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: fd });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) { errors.push(file.name + ' — ' + (j.message || r.status)); if (r.status === 400 && j.key === 'calprops.file_limit') break; continue; }
            props = j.props || props;
            added += 1;
            if (o.onProgress) o.onProgress(props);
        } catch (_) {
            errors.push(file.name);
        }
    }
    return { props, added, errors };
}

// 칸에 끌어 놓기 — 파일을 끌고 들어오면 .is-drop, 놓으면 onFiles(FileList)
export function bindDrop(box, onFiles) {
    let depth = 0;
    const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
    box.addEventListener('dragenter', (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth += 1; box.classList.add('is-drop'); });
    box.addEventListener('dragover', (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; });
    box.addEventListener('dragleave', () => { depth = Math.max(0, depth - 1); if (!depth) box.classList.remove('is-drop'); });
    box.addEventListener('drop', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault(); depth = 0; box.classList.remove('is-drop');
        if (e.dataTransfer.files && e.dataTransfer.files.length) onFiles(e.dataTransfer.files);
    });
}
